/**
 * Recently viewed products are an optional preference, not required for shopping.
 * Persistent history is used only when Shopify permits preference processing.
 */
export class RecentlyViewed {
  static #STORAGE_KEY = 'viewedProducts';
  static #MAX_PRODUCTS = 4;
  static #initialized = false;
  static #loadAttempted = false;
  static #eventPermission = null;
  static #lastPermission = null;
  static #pendingProduct = null;
  static #revision = 0;

  static changedEventName = 'cozy:recently-viewed-changed';

  static get revision() {
    return this.#revision;
  }

  /** Register once; do not treat an unavailable API as permission. */
  static initialize() {
    if (!this.#initialized) {
      this.#initialized = true;
      document.addEventListener('visitorConsentCollected', (event) => {
        const allowed = event.detail?.preferencesAllowed;
        if (typeof allowed === 'boolean') this.#eventPermission = allowed;
        this.#refreshPermission();
      });
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.#loadPrivacyAPI(), { once: true });
      }
    }
    this.#loadPrivacyAPI();
  }

  static #loadPrivacyAPI() {
    if (typeof window.Shopify?.customerPrivacy?.preferencesProcessingAllowed === 'function') {
      this.#refreshPermission();
      return;
    }
    if (this.#loadAttempted || typeof window.Shopify?.loadFeatures !== 'function') return;
    this.#loadAttempted = true;
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (!error) this.#refreshPermission();
        if (error || this.#permission() === null) this.#loadAttempted = false;
      });
    } catch {
      this.#loadAttempted = false;
      // A blocked or unavailable feature must not break search or permit storage.
    }
  }

  /** @returns {boolean | null} Null means permission has not been established. */
  static #permission() {
    if (this.#eventPermission === false) return false;
    try {
      const privacy = window.Shopify?.customerPrivacy;
      if (typeof privacy?.preferencesProcessingAllowed !== 'function') return null;
      const allowed = privacy.preferencesProcessingAllowed();
      return typeof allowed === 'boolean' ? allowed : null;
    } catch {
      return null;
    }
  }

  static #refreshPermission() {
    const allowed = this.#permission();
    const previous = this.#lastPermission;
    this.#lastPermission = allowed;
    if (allowed === false && previous !== false) {
      this.#removeStoredProducts();
      this.#notifyChanged();
    } else if (allowed === null && previous === true) {
      // Hide cached history if permission becomes unknown, without deleting a
      // previously consented preference during a temporary API failure.
      this.#notifyChanged();
    } else if (allowed === true) {
      this.#savePendingProduct();
    }
    return allowed;
  }

  static #notifyChanged() {
    this.#revision += 1;
    document.dispatchEvent(new CustomEvent(this.changedEventName));
  }

  static #removeStoredProducts() {
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
    } catch {
      // Storage can be unavailable in private/restricted browsing.
    }
  }

  /** Read only after permission has been established by the caller. */
  static #readStoredProducts() {
    try {
      const stored = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
      if (!Array.isArray(stored)) return [];
      return [...new Set(stored.filter((id) => typeof id === 'string' && /^\d+$/.test(id)))].slice(0, this.#MAX_PRODUCTS);
    } catch {
      return [];
    }
  }

  static #savePendingProduct() {
    if (!this.#pendingProduct) return;
    const previous = this.#readStoredProducts();
    const next = [this.#pendingProduct, ...previous.filter((id) => id !== this.#pendingProduct)].slice(0, this.#MAX_PRODUCTS);
    if (JSON.stringify(previous) === JSON.stringify(next)) return;
    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(next));
      this.#notifyChanged();
    } catch {
      // Optional history must never interrupt product or search interactions.
    }
  }

  /** Keep the current product in memory until preference processing is allowed. */
  static addProduct(productId) {
    if (typeof productId !== 'string' || !/^\d+$/.test(productId)) return;
    this.#pendingProduct = productId;
    this.initialize();
    this.#refreshPermission();
  }

  static clearProducts() {
    this.#pendingProduct = null;
    this.#removeStoredProducts();
    this.#notifyChanged();
  }

  static getProducts() {
    this.initialize();
    if (this.#refreshPermission() !== true) return [];
    return this.#readStoredProducts();
  }
}

RecentlyViewed.initialize();
